// KSK063 — per-element kiosk colours.
//
// Each kiosk element a merchant can recolour is a "slot". Its overrides come
// from branch_info.kiosk_custom_colors ({ slot: { outline, font, accent } }),
// which App.vue writes onto <html> as --ksk-c-<slot>-<part>. An element joins a
// slot with v-bind="kskSlot('slot')": that tags it (data-ksk-slot, used by the
// Minty preview to know what was clicked) and points its local
// --ksk-slot-outline / -font / -accent at the slot's variables. The element's
// CSS reads var(--ksk-slot-accent, <theme value>), so a slot with no override
// keeps the theme look, and an element never picks up its parent's slot.
//
//   outline — border colour
//   font    — text / icon colour
//   accent  — fill / highlight colour

export const KIOSK_COLOR_PARTS = ["outline", "font", "accent"];

// key → label shown in the Minty colour popup.
export const KIOSK_COLOR_SLOTS = {
  button_primary: "Primary buttons",
  button_outlined: "Outlined buttons",
  button_muted: "Grey buttons",
  button_text: "Text buttons",
  header_dining: "Header: Dine In / Takeaway",
  header_signin: "Header: Sign In",
  header_member: "Header: Member / Log Out",
  category_tab: "Category tabs",
  item_card: "Menu item cards",
  cart_bar: "Cart bar",
  option_tile: "Item options",
  stepper: "Quantity steppers",
  dining_tile: "Dine In / Takeaway choice",
  payment_method: "Payment methods",
  popup: "Popups",
};

// KSK117 — plain background colours, kept in the same kiosk_custom_colors JSON
// as { key: { accent: '#rrggbb' } }. Not pickable in the preview: Minty sets
// them from its own "Background colours" card. Like KSK105, the colour is
// shown as its 6% tint over white, never raw. The area reads
// var(--ksk-c-<key>-accent, <theme value>), so unset keeps the current look.
export const KIOSK_BACKGROUND_SLOTS = {
  bg_header: "Header",
  bg_category: "Menu category",
  bg_menu: "Menu listing",
};

export function kskSlot(slot) {
  const style = {};
  KIOSK_COLOR_PARTS.forEach((part) => {
    style[`--ksk-slot-${part}`] = `var(--ksk-c-${slot}-${part})`;
  });
  return { "data-ksk-slot": slot, style };
}

const HEX = /^#[0-9a-f]{6}$/i;

// KSK105 — the merchant's kiosk background colour is never painted raw: it is
// mixed 6% into white and replaces every kiosk background (page, header, bars,
// cards, popups), so text stays readable whatever colour is picked and cards
// stand apart by their shadow only. Null = original look.
export const KIOSK_BACKGROUND_TINT = 0.06;

export function kioskBackground(color) {
  if (!HEX.test(color || "")) return null;
  const mix = (i) => {
    const c = parseInt(color.slice(i, i + 2), 16);
    return Math.round(c * KIOSK_BACKGROUND_TINT + 255 * (1 - KIOSK_BACKGROUND_TINT))
      .toString(16)
      .padStart(2, "0");
  };
  return `#${mix(1)}${mix(3)}${mix(5)}`;
}

// { slot: { part: '#rrggbb' } } → { '--ksk-c-slot-part': '#rrggbb' }, keeping
// only known slots / parts and well-formed colours.
export function kioskColorVars(colors) {
  const vars = {};
  if (!colors || typeof colors !== "object") return vars;
  Object.keys(KIOSK_COLOR_SLOTS).forEach((slot) => {
    const parts = colors[slot];
    if (!parts || typeof parts !== "object") return;
    KIOSK_COLOR_PARTS.forEach((part) => {
      if (HEX.test(parts[part] || "")) vars[`--ksk-c-${slot}-${part}`] = parts[part];
    });
  });
  Object.keys(KIOSK_BACKGROUND_SLOTS).forEach((slot) => {
    const accent = colors[slot] && colors[slot].accent;
    const tint = kioskBackground(accent);
    if (tint) vars[`--ksk-c-${slot}-accent`] = tint;
  });
  return vars;
}
